<script setup>
import { computed, ref, watch } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { LineChart, ScatterChart } from 'echarts/charts'
import { AriaComponent, DataZoomComponent, GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { buildHistoricalTankChartOptions, formatShiftInterval } from './historicalTankChartOptions.js'

use([CanvasRenderer, LineChart, ScatterChart, AriaComponent, DataZoomComponent, GridComponent, TooltipComponent])

const props = defineProps({
  series: { type: Object, required: true },
})

const chart = ref(null)
const zoom = ref({ start: 0, end: 100 })
const option = computed(() => buildHistoricalTankChartOptions(props.series, zoom.value))
const deliveryCount = computed(() => props.series.events?.filter((event) => event.type === 'delivery').length ?? 0)
const ariaLabel = computed(() => {
  const tank = props.series.tank
  return `Графік залишку резервуара №${tank.tankNumber}, ${tank.productName || 'невідомий продукт'}. Надходжень: ${deliveryCount.value}.`
})

watch(() => [props.series.session, props.series.tank?.tankNumber], () => {
  zoom.value = { start: 0, end: 100 }
})

function handleDataZoom(payload) {
  const batch = payload?.batch?.[0] ?? payload
  if (!Number.isFinite(batch?.start) || !Number.isFinite(batch?.end)) return
  zoom.value = { start: batch.start, end: batch.end }
}

function setZoom(start, end) {
  const safeStart = Math.max(0, Math.min(100, start))
  const safeEnd = Math.max(safeStart + 1, Math.min(100, end))
  zoom.value = { start: safeStart, end: safeEnd }
  chart.value?.dispatchAction({ type: 'dataZoom', start: safeStart, end: safeEnd })
}

function zoomBy(delta) {
  const width = zoom.value.end - zoom.value.start
  const nextWidth = Math.max(5, Math.min(100, width + delta))
  const center = (zoom.value.start + zoom.value.end) / 2
  setZoom(center - nextWidth / 2, center + nextWidth / 2)
}

function fullShift() {
  setZoom(0, 100)
}

function handleKeyboard(event) {
  if (event.key === '+' || event.key === '=') {
    event.preventDefault()
    zoomBy(-20)
  } else if (event.key === '-') {
    event.preventDefault()
    zoomBy(20)
  } else if (event.key === 'Home' || event.key === '0') {
    event.preventDefault()
    fullShift()
  }
}
</script>

<template>
  <div class="historical-chart-shell">
    <div
      class="historical-chart-focus"
      role="img"
      tabindex="0"
      :aria-label="ariaLabel"
      @keydown="handleKeyboard"
    >
      <p class="visually-hidden">Використовуйте + і − для масштабу, Home або 0 — щоб показати всю зміну. Нижній огляд можна пересувати мишею або дотиком.</p>
      <VChart
        ref="chart"
        class="historical-tank-chart"
        :option="option"
        :autoresize="{ throttle: 50 }"
        @datazoom="handleDataZoom"
      />
    </div>
    <div class="chart-delivery-legend"><span aria-hidden="true"></span><strong>Надходження</strong><small>різкий зелений сегмент і ромб</small></div>
    <div class="chart-navigation-row">
      <div class="chart-zoom-controls" aria-label="Масштаб графіка">
        <button type="button" title="Зменшити масштаб" aria-label="Зменшити масштаб" @click="zoomBy(20)">−</button>
        <button type="button" title="Збільшити масштаб" aria-label="Збільшити масштаб" @click="zoomBy(-20)">+</button>
        <button type="button" class="full-shift-button" @click="fullShift">Уся зміна</button>
      </div>
      <p class="historical-shift-interval">{{ formatShiftInterval(series.interval) }}</p>
    </div>
  </div>
</template>
